/**
 * DynamicPage.tsx
 *
 * Renders a page built in the Journey Builder.
 *
 * The page definition comes from the /api/journey/current-page/ endpoint:
 *   { id, name, slug, route, components: ["comp_id_1", ...], layout, ... }
 *
 * Components are fetched via the orchestrator (/api/orchestrator/ mode=get)
 * and rendered using the componentRegistry.
 *
 * This is the V3 equivalent of a hardcoded page component like PersonalInfo.tsx.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import componentRegistry from "@/registry/componentRegistry";
import { useAuth } from "@/contexts/AuthContext";
import FullScreenLoader from "@/components/FullScreenLoader";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PageDefinition {
  id: number;
  name: string;
  slug: string;
  route: string;
  components: string[];   // ordered list of component IDs
  layout: Record<string, any>;
  is_pre_login: boolean;
  requires_auth: boolean;
  step_index: number;
  total_steps: number;
  journey_config_id: number;
}

interface ComponentData {
  [componentId: string]: Record<string, any>;
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchCurrentPage(): Promise<PageDefinition | null> {
  const res = await fetch("/api/journey/current-page/", {
    credentials: "include",
  });
  if (res.status === 204 || res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to fetch current page: ${res.status}`);
  return res.json();
}

async function fetchComponentData(componentIds: string[]): Promise<ComponentData> {
  if (componentIds.length === 0) return {};
  const res = await fetch("/api/orchestrator/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode: "get", components: componentIds }),
  });
  if (!res.ok) return {};
  return res.json();
}

async function advanceJourney(pageId?: number): Promise<PageDefinition | null> {
  const body = pageId ? { page_id: pageId } : {};
  const res = await fetch("/api/journey/advance/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.status === 204) return null;  // Journey complete
  if (!res.ok) throw new Error(`Failed to advance journey: ${res.status}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// DynamicPage component
// ---------------------------------------------------------------------------

const DynamicPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [page, setPage] = useState<PageDefinition | null>(null);
  const [componentData, setComponentData] = useState<ComponentData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [advancing, setAdvancing] = useState(false);

  // -------------------------------------------------------------------------
  // Load current page + component data
  // -------------------------------------------------------------------------
  const loadPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const pageDef = await fetchCurrentPage();
      if (!pageDef) {
        // Journey complete — navigate to success/completion page
        navigate("/loan-success", { replace: true });
        return;
      }
      setPage(pageDef);

      // Fetch data for all components on this page
      const data = await fetchComponentData(pageDef.components);
      setComponentData(data);
    } catch (err: any) {
      console.error("[DynamicPage] Failed to load page:", err);
      setError(err.message || "Failed to load page");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user) {
      loadPage();
    }
  }, [user, loadPage]);

  // -------------------------------------------------------------------------
  // Advance to next page
  // -------------------------------------------------------------------------
  const handleAdvance = useCallback(
    async (nextPageId?: number) => {
      if (advancing) return;
      setAdvancing(true);
      try {
        const nextPage = await advanceJourney(nextPageId);
        if (!nextPage) {
          // Journey complete
          navigate("/loan-success", { replace: true });
          return;
        }
        // Navigate to the next page's route
        navigate(nextPage.route, { replace: true });
        setPage(nextPage);
        const data = await fetchComponentData(nextPage.components);
        setComponentData(data);
      } catch (err: any) {
        console.error("[DynamicPage] Failed to advance journey:", err);
        setError(err.message || "Failed to advance journey");
      } finally {
        setAdvancing(false);
      }
    },
    [advancing, navigate]
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) {
    return <FullScreenLoader loading={true} />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-intellend-white">
        <div className="text-center p-8">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Something went wrong
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadPage}
            className="bg-intellend-green text-white px-6 py-2 rounded-md hover:bg-intellend-green/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!page) return null;

  return (
    <div className="min-h-screen bg-intellend-white">
      {/* Progress indicator */}
      {page.total_steps > 0 && (
        <div className="w-full bg-gray-200 h-1">
          <div
            className="bg-intellend-green h-1 transition-all duration-300"
            style={{
              width: `${Math.round(((page.step_index + 1) / page.total_steps) * 100)}%`,
            }}
          />
        </div>
      )}

      {/* Page content: render each component in order */}
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {page.components.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <p>This page has no components configured.</p>
            <p className="text-sm mt-2">
              Use the Journey Builder to add components to this page.
            </p>
          </div>
        ) : (
          page.components.map((componentId) => {
            const registryEntry = componentRegistry[componentId];
            if (!registryEntry) {
              console.warn(
                `[DynamicPage] Component '${componentId}' not found in registry`
              );
              return (
                <div
                  key={componentId}
                  className="p-4 border border-dashed border-gray-300 rounded-md text-gray-400 text-sm my-2"
                >
                  Unknown component: {componentId}
                </div>
              );
            }

            const ComponentToRender = registryEntry.component;
            const compData = componentData[componentId] || {};

            return (
              <div key={componentId} className="mb-4">
                <ComponentToRender
                  {...(registryEntry.defaultProps || {})}
                  data={compData}
                  onAdvance={handleAdvance}
                  pageSlug={page.slug}
                  stepIndex={page.step_index}
                  totalSteps={page.total_steps}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DynamicPage;
